import { useCallback, useEffect, useState } from 'react';
import { labCycleAPI, labFleetAPI } from '../services/api';
import waitForLabCycleAction from './waitForLabCycleAction';

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export default function useLabFleet({ extendExpiry = false } = {}) {
  const [fleet, setFleet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [actionKey, setActionKey] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [trackedJob, setTrackedJob] = useState(null);
  const [progressError, setProgressError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionReport, setActionReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await labFleetAPI.get();
      setFleet(res.data);
      setError('');
    } catch (err) {
      setError(errorMessage(err, 'Could not load server accounts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const scan = useCallback(async () => {
    setScanning(true);
    setError('');
    try {
      await labFleetAPI.scan();
      const started = Date.now();
      while (Date.now() - started < 8 * 60 * 1000) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const progress = await labFleetAPI.progress();
        const status = progress.data?.status;
        if (status === 'finished' || status === 'failed' || status === 'idle') {
          if (status === 'failed') {
            setError(progress.data?.error?.message || 'Server fetch failed');
          }
          break;
        }
      }
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not fetch the servers'));
    } finally {
      setScanning(false);
    }
  }, [load]);

  const act = useCallback(
    async (account, action, options = {}) => {
      if (action === 'delete' && trackedJob?.jobId) {
        setProgressError(
          `Delete for ${trackedJob.unixUsername || account.unixUsername} is already job ${trackedJob.jobId}. Check progress again. A new delete was not started.`
        );
        return;
      }
      if (action === 'delete' && options.keepBackup !== true && options.keepBackup !== false) {
        setDeleteTarget(account);
        return;
      }
      const confirmed =
        action === 'delete' ||
        window.confirm(
          action === 'enable'
            ? extendExpiry
              ? `Enable ${account.unixUsername} on ${account.alias || 'this server'}? This unlocks the login, starts VNC, sets the account expiry 45 days from today, and restarts the password age. The password itself is not changed.`
              : `Enable ${account.unixUsername} on ${account.alias || 'this server'}? This unlocks the login and starts VNC. Account expiry and an expired password are not reset.`
            : `Disable ${account.unixUsername}? This locks the login and stops VNC. The home stays.`
        );
      if (!confirmed) return;
      if (action === 'delete') setDeleteTarget(null);
      setActionKey(`${account.unixUsername}:${action}`);
      setError('');
      setNotice('');
      setProgressError('');
      setActionReport(null);
      let holdJob = false;
      try {
        if (action === 'enable') {
          const res = await labFleetAPI.enable({
            unixUsername: account.unixUsername,
            alias: account.alias,
            home: account.home,
          });
          setNotice(
            `${res.data?.message || `Enabled ${account.unixUsername}.`} Fetch from servers to refresh Account expires.`
          );
        } else {
          const res = await labCycleAPI.act(account.unixUsername, action, {
            keepBackup: options.keepBackup === true,
          });
          const jobId = res?.data?.jobId;
          if (res?.status === 202 && jobId) {
            setTrackedJob({
              jobId,
              unixUsername: account.unixUsername,
              action,
              status: 'running',
              step: 'starting',
              phase: 'precheck',
              outcome: 'running',
            });
          }
          const outcome = await waitForLabCycleAction(res, (job) => {
            setTrackedJob({ ...job, unixUsername: account.unixUsername, action });
            if (job?.progressUnavailable) {
              setProgressError('The last status check did not reach the server. Checking the same job again.');
            } else {
              setProgressError('');
            }
          });
          setTrackedJob(null);
          setProgressError('');
          setActionReport(outcome);
        }
        await load();
      } catch (err) {
        if (err.actionReport) {
          setTrackedJob(null);
          setProgressError('');
          setActionReport(err.actionReport);
        } else if (err.progressLost) {
          holdJob = err.statusCode !== 401 && err.statusCode !== 404;
          setProgressError(err.message);
          if (!holdJob) setTrackedJob(null);
          else if (err.lastJob) setTrackedJob({ ...err.lastJob, unixUsername: account.unixUsername, action });
        } else {
          setError(errorMessage(err, `${action} failed for ${account.unixUsername}`));
        }
      } finally {
        if (!holdJob) setActionKey('');
      }
    },
    [load, extendExpiry, trackedJob]
  );

  const resumeProgress = useCallback(async () => {
    if (!trackedJob?.jobId) return;
    const account = { unixUsername: trackedJob.unixUsername };
    const action = trackedJob.action || 'delete';
    setActionKey(`${account.unixUsername}:${action}`);
    setProgressError('');
    setError('');
    let holdJob = true;
    try {
      const outcome = await waitForLabCycleAction({ status: 202, data: { jobId: trackedJob.jobId } }, (job) => {
        setTrackedJob({ ...job, unixUsername: account.unixUsername, action });
      });
      holdJob = false;
      setTrackedJob(null);
      setActionReport(outcome);
      await load();
    } catch (err) {
      if (err.actionReport) {
        holdJob = false;
        setTrackedJob(null);
        setActionReport(err.actionReport);
      } else if (err.progressLost) {
        holdJob = err.statusCode !== 401 && err.statusCode !== 404;
        setProgressError(err.message);
        if (!holdJob) setTrackedJob(null);
        else if (err.lastJob) setTrackedJob({ ...err.lastJob, unixUsername: account.unixUsername, action });
      } else {
        holdJob = false;
        setError(errorMessage(err, 'Could not read the server action'));
      }
    } finally {
      if (!holdJob) setActionKey('');
    }
  }, [trackedJob, load]);

  const download = useCallback(async ({ recommendation = 'all', q = '' } = {}) => {
    setDownloading(true);
    setError('');
    try {
      const res = await labFleetAPI.exportExcel({
        recommendation: recommendation === 'all' ? undefined : recommendation,
        q: q || undefined,
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `server-accounts-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = errorMessage(err, 'Could not download the Excel file');
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.message || message;
        } catch {
          /* keep the fallback */
        }
      }
      setError(message);
    } finally {
      setDownloading(false);
    }
  }, []);

  return {
    fleet,
    loading,
    scanning,
    downloading,
    actionKey,
    error,
    notice,
    actionReport,
    reportNotice: setNotice,
    load,
    scan,
    act,
    deleteTarget,
    cancelDelete: () => setDeleteTarget(null),
    trackedJob,
    progressError,
    resumeProgress,
    download,
    dismissActionReport: () => setActionReport(null),
  };
}
